import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight, MapPin, FileText, Zap, CheckCircle, Mail, Send, Building2, HardHat, Ruler, ChevronRight, BarChart3, Target, Clock, Shield, Trees, Hammer, Home, Landmark, PenTool, Warehouse, X } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

import { API_BASE as API, STATIC_BASE } from '../utils/api';
const S = STATIC_BASE;
// Builder flyer always first, rest randomized
const HERO_FLYER_MAIN = { src: `${S}/api/static/uflyer6.jpg`, alt: 'London Builders construction flyer' };
const HERO_FLYER_POOL = [
  { src: `${S}/api/static/uflyer7.jpg`, alt: 'FlashBuilder dream home flyer' },
  { src: `${S}/api/static/uflyer2.jpg`, alt: 'Plumbing services marketing flyer' },
  { src: `${S}/api/static/uflyer3.jpg`, alt: 'Professional plumbing services flyer' },
  { src: `${S}/api/static/uflyer4.jpg`, alt: 'Doors and windows services flyer' },
  { src: `${S}/api/static/uflyer5.jpg`, alt: 'Construction company promotion flyer' },
  { src: `${S}/api/static/uflyer1.jpg`, alt: 'London Builders yellow flyer' },
];

const APP_TYPES = [
  { key: 'extension', label: 'Extensions', count: '154k', icon: Ruler, color: 'bg-emerald-50 text-emerald-600' },
  { key: 'loft_conversion', label: 'Loft Conversions', count: '37k', icon: Home, color: 'bg-purple-50 text-purple-600' },
  { key: 'conversion', label: 'Conversions', count: '41k', icon: HardHat, color: 'bg-amber-50 text-amber-600' },
  { key: 'commercial', label: 'Commercial', count: '23k', icon: Building2, color: 'bg-blue-50 text-blue-600' },
  { key: 'new_build', label: 'New Builds', count: '3.4k', icon: Warehouse, color: 'bg-teal-50 text-teal-600' },
  { key: 'tree_works', label: 'Tree Works', count: '36k', icon: Trees, color: 'bg-green-50 text-green-600' },
  { key: 'alterations', label: 'Alterations', count: '18k', icon: PenTool, color: 'bg-rose-50 text-rose-600' },
  { key: 'demolition', label: 'Demolitions', count: '9k', icon: Hammer, color: 'bg-red-50 text-red-600' },
  { key: 'listed_building', label: 'Listed Buildings', count: '3.8k', icon: Landmark, color: 'bg-orange-50 text-orange-600' },
  { key: 'outbuilding', label: 'Outbuildings', count: '9.2k', icon: Building2, color: 'bg-sky-50 text-sky-600' },
];

export default function HomePage() {
  const [postcode, setPostcode] = useState('');
  const navigate = useNavigate();

  // Builder flyer always first, pick 4 random others
  const [heroFlyers] = useState(() => {
    const shuffled = [...HERO_FLYER_POOL].sort(() => Math.random() - 0.5);
    return [HERO_FLYER_MAIN, ...shuffled.slice(0, 4)];
  });
  const [flyersLoaded, setFlyersLoaded] = useState(false);
  const [lightboxFlyer, setLightboxFlyer] = useState(null);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(postcode.trim() ? `/map?q=${encodeURIComponent(postcode.trim())}` : '/map');
  };

  useSEO({
    title: 'PlanningSearch | Find Building Projects & Send Letters Automatically',
    description: 'Discover live UK planning applications and send letters directly to applicants. AI-powered insights, automated outreach, and project value estimates. Used by builders, architects, and tradespeople across the UK.',
    canonical: 'https://planningsearch.com/',
  });

  // Trigger hero flyer animation after short delay
  useEffect(() => {
    const t = setTimeout(() => setFlyersLoaded(true), 300);
    return () => clearTimeout(t);
  }, []);

  // Scroll-triggered animation for flyer stack
  const flyerRef = useRef(null);
  useEffect(() => {
    const el = flyerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('animate'); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">

      {/* ═══════════ HERO ═══════════ */}
      <section className="relative overflow-hidden bg-zinc-950" data-testid="hero-section">
        {/* Background — subtle gradient instead of image for cleaner look with flyers */}
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950" />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 md:py-28 lg:py-32">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            {/* Left: Copy + search */}
            <div className="flex-1 max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 border border-white/10 backdrop-blur-sm rounded-full text-emerald-400 text-xs font-semibold mb-6">
                <Target className="w-3 h-3" /> Outreach platform for the building trade
              </div>
              <h1 className="font-['Manrope'] text-3xl sm:text-4xl md:text-5xl lg:text-[3.4rem] tracking-tight font-extrabold text-white mb-4 sm:mb-5 leading-[1.1]">
                Find projects.<br />
                <span className="text-emerald-400">Send letters automatically.</span>
              </h1>
              <p className="text-sm sm:text-base lg:text-lg text-zinc-400 mb-6 sm:mb-8 leading-relaxed max-w-lg">
                Track live planning applications across the UK. Get AI insights on every project, then send professionally printed letters to applicants — directly from the platform.
              </p>
              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row max-w-md">
                <div className="relative flex-1">
                  <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type="text"
                    placeholder="Enter postcode or area..."
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value)}
                    className="w-full pl-10 pr-4 py-3.5 bg-white text-zinc-900 text-sm border-0 rounded-t-lg sm:rounded-t-none sm:rounded-l-lg placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="postcode-search-input"
                  />
                </div>
                <button
                  type="submit"
                  className="bg-emerald-600 text-white font-semibold px-7 py-3.5 text-sm rounded-b-lg sm:rounded-b-none sm:rounded-r-lg hover:bg-emerald-500 transition-colors flex items-center justify-center gap-2"
                  data-testid="postcode-search-button"
                >
                  <Search className="w-4 h-4" /> Search Free
                </button>
              </form>
              <p className="text-xs text-zinc-500 mt-3">Free to search. No credit card required.</p>
            </div>

            {/* Right: Cascading flyer showcase — 5 flyers, clickable */}
            <div className="flex flex-1 items-center justify-center mt-6 lg:mt-0" data-testid="hero-flyer-stack">
              {/* Desktop: wide cascade */}
              <div className="hidden lg:block relative" style={{ width: 520, height: 500 }}>
                {/* Flyer 5 — far back left */}
                <button onClick={() => setLightboxFlyer(heroFlyers[4])} className={`absolute rounded-lg overflow-hidden shadow-xl shadow-black/40 border border-white/5 transition-all duration-[1.2s] ease-out cursor-pointer hover:opacity-90 hover:scale-105 ${flyersLoaded ? 'opacity-60 translate-y-0' : 'opacity-0 translate-y-20'}`}
                  style={{ width: 160, height: 224, top: 120, left: 0, transform: flyersLoaded ? 'rotate(-12deg)' : 'rotate(0deg)', transitionDelay: '0.5s', zIndex: 1 }}>
                  <img src={heroFlyers[4]?.src} alt={heroFlyers[4]?.alt} className="w-full h-full object-cover" />
                </button>
                {/* Flyer 4 — back left */}
                <button onClick={() => setLightboxFlyer(heroFlyers[3])} className={`absolute rounded-lg overflow-hidden shadow-xl shadow-black/40 border border-white/10 transition-all duration-[1.1s] ease-out cursor-pointer hover:opacity-95 hover:scale-105 ${flyersLoaded ? 'opacity-75 translate-y-0' : 'opacity-0 translate-y-20'}`}
                  style={{ width: 180, height: 252, top: 80, left: 55, transform: flyersLoaded ? 'rotate(-6deg)' : 'rotate(0deg)', transitionDelay: '0.35s', zIndex: 2 }}>
                  <img src={heroFlyers[3]?.src} alt={heroFlyers[3]?.alt} className="w-full h-full object-cover" />
                </button>
                {/* Flyer 1 — CENTER MAIN (builder flyer) */}
                <button onClick={() => setLightboxFlyer(heroFlyers[0])} className={`absolute rounded-xl overflow-hidden shadow-2xl shadow-black/60 border-2 border-emerald-500/40 transition-all duration-1000 ease-out cursor-pointer hover:scale-[1.03] ${flyersLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
                  style={{ width: 240, height: 336, top: 0, left: 140, zIndex: 5 }}>
                  <img src={heroFlyers[0]?.src} alt={heroFlyers[0]?.alt} className="w-full h-full object-cover" />
                  <div className="absolute top-3 right-3 bg-emerald-500 text-white text-[7px] font-bold px-2 py-0.5 rounded-full shadow-lg uppercase tracking-wider">
                    Your design
                  </div>
                </button>
                {/* Flyer 3 — back right */}
                <button onClick={() => setLightboxFlyer(heroFlyers[2])} className={`absolute rounded-lg overflow-hidden shadow-xl shadow-black/40 border border-white/10 transition-all duration-[1.1s] ease-out cursor-pointer hover:opacity-95 hover:scale-105 ${flyersLoaded ? 'opacity-75 translate-y-0' : 'opacity-0 translate-y-20'}`}
                  style={{ width: 180, height: 252, top: 85, left: 290, transform: flyersLoaded ? 'rotate(6deg)' : 'rotate(0deg)', transitionDelay: '0.25s', zIndex: 3 }}>
                  <img src={heroFlyers[2]?.src} alt={heroFlyers[2]?.alt} className="w-full h-full object-cover" />
                </button>
                {/* Flyer 2 — far back right */}
                <button onClick={() => setLightboxFlyer(heroFlyers[1])} className={`absolute rounded-lg overflow-hidden shadow-xl shadow-black/40 border border-white/5 transition-all duration-[1.2s] ease-out cursor-pointer hover:opacity-90 hover:scale-105 ${flyersLoaded ? 'opacity-60 translate-y-0' : 'opacity-0 translate-y-20'}`}
                  style={{ width: 160, height: 224, top: 125, left: 365, transform: flyersLoaded ? 'rotate(12deg)' : 'rotate(0deg)', transitionDelay: '0.45s', zIndex: 1 }}>
                  <img src={heroFlyers[1]?.src} alt={heroFlyers[1]?.alt} className="w-full h-full object-cover" />
                </button>
                {/* Glow */}
                <div className={`absolute rounded-full bg-emerald-500/10 blur-3xl transition-opacity duration-[1.5s] ${flyersLoaded ? 'opacity-100' : 'opacity-0'}`}
                  style={{ width: 280, height: 220, top: 140, left: 120, zIndex: 0, transitionDelay: '0.6s' }} />
              </div>

              {/* Mobile: infinite scrolling marquee */}
              <div className="lg:hidden w-full overflow-hidden -mx-4 px-0">
                <div className="hero-marquee flex gap-3 py-2">
                  {[...heroFlyers, ...heroFlyers].map((flyer, i) => (
                    <button key={i} onClick={() => setLightboxFlyer(flyer)}
                      className="flex-shrink-0 rounded-lg overflow-hidden shadow-xl shadow-black/40 border border-white/10 cursor-pointer active:scale-95 relative"
                      style={{ width: 130, height: 182 }}>
                      <img src={flyer.src} alt={flyer.alt} className="w-full h-full object-cover" />
                      {i % heroFlyers.length === 0 && <div className="absolute top-1.5 right-1.5 bg-emerald-500 text-white text-[5px] font-bold px-1 py-0.5 rounded-full shadow uppercase tracking-wider">Your design</div>}
                    </button>
                  ))}
                </div>
                <style>{`
                  .hero-marquee {
                    animation: heroMarquee 20s linear infinite;
                    width: max-content;
                  }
                  .hero-marquee:active { animation-play-state: paused; }
                  @keyframes heroMarquee {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                  }
                `}</style>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ FLYER LIGHTBOX ═══════════ */}
      {lightboxFlyer && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setLightboxFlyer(null)} data-testid="flyer-lightbox">
          <div className="relative max-w-lg w-full max-h-[85vh] animate-in fade-in zoom-in-95" onClick={(e) => e.stopPropagation()}>
            <img src={lightboxFlyer.src} alt={lightboxFlyer.alt} className="w-full h-auto rounded-xl shadow-2xl" />
            <button onClick={() => setLightboxFlyer(null)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-zinc-600 hover:text-zinc-900 transition-colors"
              data-testid="lightbox-close">
              <X className="w-4 h-4" />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
              <button onClick={() => navigate('/letters')}
                className="bg-emerald-600 text-white font-semibold px-5 py-2 rounded-lg text-xs hover:bg-emerald-500 transition-colors shadow-lg inline-flex items-center gap-1.5">
                <Send className="w-3 h-3" /> Design yours in Letter Studio
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ SOCIAL PROOF BAR ═══════════ */}
      <section className="border-b border-zinc-100 bg-white" data-testid="social-proof-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 items-center gap-x-6 sm:gap-x-0 gap-y-3">
            {[
              { val: '525,000+', label: 'Applications tracked' },
              { val: '443', label: 'UK councils covered' },
              { val: '9', label: 'Letter designs' },
              { val: 'AI', label: 'Intelligence reports' },
            ].map(({ val, label }) => (
              <div key={label} className="flex items-center gap-2.5 sm:justify-center">
                <span className="font-['Manrope'] text-base sm:text-lg font-extrabold text-zinc-900">{val}</span>
                <span className="text-[11px] sm:text-xs text-zinc-500">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ BROWSE BY PROJECT TYPE ═══════════ */}
      <section className="bg-zinc-50 border-b border-zinc-100" data-testid="app-types-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14 md:py-16">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-1.5">Browse by project type</p>
              <h2 className="font-['Manrope'] text-lg sm:text-xl font-bold text-zinc-900">What are you looking for?</h2>
            </div>
            <button onClick={() => navigate('/map')} className="hidden sm:flex items-center gap-1 text-sm text-emerald-700 font-medium hover:text-emerald-800 transition-colors">
              View all <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {APP_TYPES.map(({ key, label, count, icon: Icon, color }) => (
              <button
                key={key}
                onClick={() => navigate(`/map?type=${key}`)}
                className="flex flex-col items-start p-4 bg-white border border-zinc-200 rounded-lg hover:shadow-md hover:-translate-y-[1px] transition-all group text-left"
                data-testid={`browse-type-${key}`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${color.split(' ')[0]}`}>
                  <Icon className={`w-4 h-4 ${color.split(' ')[1]}`} />
                </div>
                <span className="text-sm font-semibold text-zinc-800 mb-0.5">{label}</span>
                <span className="text-[11px] text-zinc-400">{count} applications</span>
              </button>
            ))}
          </div>
          <button onClick={() => navigate('/map')} className="sm:hidden mt-4 w-full py-2.5 text-sm text-emerald-700 font-medium border border-emerald-200 rounded-lg bg-emerald-50 hover:bg-emerald-100 transition-colors flex items-center justify-center gap-1">
            View all types <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </section>

      {/* ═══════════ AUTOMATED OUTREACH — FLYER SHOWCASE ═══════════ */}
      <section className="bg-zinc-950 overflow-hidden" data-testid="outreach-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 md:py-24">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-0 items-center">

            {/* Left: Copy */}
            <div className="flex-1 max-w-lg lg:pr-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-emerald-400 mb-3">Automated Outreach</p>
              <h2 className="font-['Manrope'] text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-4 leading-tight">
                Print & post your<br />
                <span className="text-emerald-400">own flyers automatically</span>
              </h2>
              <p className="text-sm sm:text-base text-zinc-400 leading-relaxed mb-8">
                Design your flyer in Canva — or use one of our templates — and we'll print it and post it directly to planning applicants. Your marketing on their doormat in 48 hours.
              </p>

              <div className="grid grid-cols-2 gap-3 mb-8">
                {[
                  { label: 'Upload your Canva design', sub: 'PNG, JPG, or PDF' },
                  { label: 'Or use our AI writer', sub: '9 built-in templates' },
                  { label: 'We print & post it', sub: '1-3 working days' },
                  { label: 'From £0.75 per letter', sub: 'Bulk discounts on Pro' },
                ].map(({ label, sub }) => (
                  <div key={label} className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <p className="text-xs font-semibold text-white mb-0.5">{label}</p>
                    <p className="text-[11px] text-zinc-500">{sub}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3">
                <button onClick={() => navigate('/letters')} className="bg-emerald-600 text-white font-semibold px-6 py-2.5 rounded-lg text-sm hover:bg-emerald-500 transition-colors inline-flex items-center gap-2" data-testid="explore-letters-btn">
                  Try the Letter Studio <ArrowRight className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => navigate('/signup')} className="border border-white/20 text-white font-medium px-5 py-2.5 rounded-lg text-sm hover:bg-white/5 transition-colors">
                  Sign up free
                </button>
              </div>
            </div>

            {/* Right: Flyer showcase — centered fanned stack with scroll animation */}
            <div className="flex-1 flex items-center justify-center">
              <div ref={flyerRef} className="relative flyer-stack" style={{ width: 380, height: 440 }}>
                {/* Back flyer (left tilt) */}
                <div className="flyer-card absolute rounded-xl overflow-hidden shadow-2xl shadow-black/50 border border-white/10"
                  style={{ width: 220, height: 310, top: 50, left: 10, '--rotate': '-8deg', zIndex: 1, '--delay': '0.15s' }}>
                  <img src={`${S}/api/static/uflyer5.jpg`} alt="Builder flyer" className="w-full h-full object-cover" loading="lazy" />
                </div>
                {/* Middle flyer (center, front) */}
                <div className="flyer-card absolute rounded-xl overflow-hidden shadow-2xl shadow-black/60 border-2 border-white/20"
                  style={{ width: 240, height: 340, top: 0, left: 70, '--rotate': '0deg', zIndex: 3, '--delay': '0s' }}>
                  <img src={`${S}/api/static/uflyer1.jpg`} alt="Construction company flyer" className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute top-3 right-3 bg-emerald-500 text-white text-[8px] font-bold px-2 py-0.5 rounded-full shadow-lg">
                    UPLOADED FROM CANVA
                  </div>
                </div>
                {/* Front flyer (right tilt) */}
                <div className="flyer-card absolute rounded-xl overflow-hidden shadow-2xl shadow-black/40 border border-white/10"
                  style={{ width: 210, height: 300, top: 60, left: 160, '--rotate': '7deg', zIndex: 2, '--delay': '0.3s' }}>
                  <img src={`${S}/api/static/uflyer4.jpg`} alt="Home services flyer" className="w-full h-full object-cover" loading="lazy" />
                </div>
                {/* Decorative glow */}
                <div className="absolute rounded-full bg-emerald-500/20 blur-3xl" style={{ width: 200, height: 200, top: 120, left: 90, zIndex: 0 }} />
              </div>
            </div>
          </div>
        </div>

        <style>{`
          .flyer-card {
            opacity: 0;
            transform: translateY(60px) scale(0.9) rotate(0deg);
          }
          .flyer-stack.animate .flyer-card {
            animation: flyerIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
            animation-delay: var(--delay, 0s);
          }
          @keyframes flyerIn {
            0% { opacity: 0; transform: translateY(60px) scale(0.88) rotate(0deg); }
            100% { opacity: 1; transform: translateY(0) scale(1) rotate(var(--rotate, 0deg)); }
          }
        `}</style>
      </section>

      {/* ═══════════ HOW IT WORKS — 4 STEP FUNNEL ═══════════ */}
      <section className="bg-zinc-50 border-y border-zinc-100" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 md:py-28">
          <div className="mb-10 sm:mb-12">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-2">How it works</p>
            <h2 className="font-['Manrope'] text-xl sm:text-2xl md:text-3xl lg:text-4xl tracking-tight font-bold text-zinc-900">
              From planning application to paying client
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {[
              { icon: MapPin, num: '1', title: 'Search your area', desc: 'Enter any UK postcode to discover live planning applications. Browse on an interactive map or search list.' },
              { icon: Zap, num: '2', title: 'AI analyses the project', desc: 'Our AI estimates project value, scores the opportunity, recommends trades, and suggests the best approach.' },
              { icon: Send, num: '3', title: 'Send a letter', desc: 'Pick a design, customise the copy (or let AI write it), and we print and post a letter to the applicant.' },
              { icon: Target, num: '4', title: 'Win the work', desc: 'Your letter lands on their doormat within 1-3 days. Be the first tradesperson they hear from.' },
            ].map(({ icon: Icon, num, title, desc }) => (
              <div key={num} className="bg-white border border-zinc-200/60 rounded-lg p-6 shadow-sm hover:shadow-md hover:-translate-y-[2px] transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                    <Icon className="w-5 h-5 text-emerald-700" />
                  </div>
                  <span className="font-['Manrope'] text-2xl font-extrabold text-zinc-200">{num}</span>
                </div>
                <h3 className="font-['Manrope'] text-base font-bold text-zinc-900 mb-2">{title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ FEATURE GRID — VALUE PROPS ═══════════ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 md:py-28" data-testid="features-section">
        <div className="text-center mb-10 sm:mb-14">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-2">Why PlanningSearch</p>
          <h2 className="font-['Manrope'] text-xl sm:text-2xl md:text-3xl lg:text-4xl tracking-tight font-bold text-zinc-900">
            Everything you need to win more projects
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { icon: Send, title: 'Printed letter outreach', desc: 'Send professionally printed letters to applicants. Choose from 9 designs, personalise the copy, or let our AI write it for you. Letters arrive in 1-3 working days.', highlight: true },
            { icon: Zap, title: 'AI Intelligence Reports', desc: 'Every application gets an AI analysis: estimated project value, opportunity score (1-10), recommended trades, and a suggested approach strategy.' },
            { icon: BarChart3, title: 'Estimated project values', desc: 'See cost breakdowns for every project — materials, labour, and total build cost. Visible for free so you can quickly assess if a job is worth pursuing.' },
            { icon: Mail, title: 'Instant email alerts', desc: 'Get notified the moment new applications matching your criteria land. Be the first to reach out, before your competitors even know the project exists.' },
            { icon: Clock, title: 'First-mover advantage', desc: 'New planning applications appear within days of submission. Paired with automated letter sending, you can reach applicants before anyone else.' },
            { icon: Shield, title: 'Professional credibility', desc: 'Custom letterheads with your logo, accreditations, and tagline. AI-generated copy tuned to your trade. Your outreach looks polished and trustworthy.' },
          ].map(({ icon: Icon, title, desc, highlight }) => (
            <div key={title} className={`rounded-lg p-6 transition-all hover:shadow-md hover:-translate-y-[1px] ${highlight ? 'bg-emerald-50 border-2 border-emerald-200' : 'bg-white border border-zinc-200 shadow-sm'}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${highlight ? 'bg-emerald-600' : 'bg-zinc-100'}`}>
                <Icon className={`w-5 h-5 ${highlight ? 'text-white' : 'text-zinc-600'}`} />
              </div>
              <h3 className="font-['Manrope'] text-base font-bold text-zinc-900 mb-2">{title}</h3>
              <p className="text-sm text-zinc-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════ OUTREACH CTA BANNER ═══════════ */}
      {/* Moved to after Browse section — see above */}

      {/* ═══════════ PRICING PREVIEW ═══════════ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 md:py-28" data-testid="pricing-preview-section">
        <div className="text-center mb-10 sm:mb-12">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-2">Pricing</p>
          <h2 className="font-['Manrope'] text-xl sm:text-2xl md:text-3xl lg:text-4xl tracking-tight font-bold text-zinc-900">
            Start free, upgrade when ready
          </h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">Every plan includes letter allowances. No hidden costs.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
          {[
            { name: 'Free', price: '0', sub: 'forever', features: ['25 views/month', 'AI reports & values', 'Postcode district only'] },
            { name: 'Starter', price: '79', sub: '/mo', features: ['200 views/month', '5 letters included', 'Full addresses'] },
            { name: 'Intelligence', price: '149', sub: '/mo', featured: true, features: ['1,000 views/month', '25 letters included', 'AI scoring & alerts'] },
            { name: 'Pro', price: '199', sub: '/mo', features: ['2,500 views/month', '50 letters included', 'Team accounts'] },
          ].map(({ name, price, sub, featured, features }) => (
            <div
              key={name}
              className={`rounded-lg p-5 transition-all ${
                featured ? 'bg-white border-2 border-emerald-600 shadow-lg relative' : 'bg-white border border-zinc-200 shadow-sm hover:shadow-md'
              }`}
            >
              {featured && <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 bg-emerald-700 text-white text-[9px] font-bold uppercase tracking-wider rounded-full">Popular</div>}
              <h3 className="font-['Manrope'] text-sm font-bold text-zinc-900">{name}</h3>
              <div className="flex items-baseline gap-1 mt-2 mb-4">
                <span className="font-['Manrope'] text-2xl font-extrabold text-zinc-900">&pound;{price}</span>
                <span className="text-xs text-zinc-400">{sub}</span>
              </div>
              <ul className="space-y-2 mb-4">
                {features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-xs">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                    <span className="text-zinc-600">{f}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate('/pricing')}
                className={`w-full py-2 text-xs font-semibold rounded-md transition-colors ${
                  featured ? 'bg-emerald-700 text-white hover:bg-emerald-800' : 'border border-zinc-200 text-zinc-700 hover:bg-zinc-50'
                }`}
                data-testid={`pricing-${name.toLowerCase()}-button`}>
                {name === 'Free' ? 'Start free' : 'Get started'}
              </button>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <button onClick={() => navigate('/pricing')} className="text-sm text-zinc-400 hover:text-emerald-700 inline-flex items-center gap-1 transition-colors" data-testid="view-all-plans-link">
            Compare all plans <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </section>

      {/* ═══════════ FINAL CTA ═══════════ */}
      <section className="bg-emerald-700" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="font-['Manrope'] text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">Your next client just submitted a planning application</h2>
          <p className="text-emerald-200 text-sm mb-6 max-w-lg mx-auto">Be the first builder they hear from. Search your area, send a letter, win the work.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button onClick={() => navigate('/signup')} className="bg-white text-emerald-700 font-semibold px-6 py-2.5 rounded-lg text-sm hover:bg-emerald-50 transition-colors" data-testid="cta-signup-btn">
              Create free account
            </button>
            <button onClick={() => navigate('/map')} className="border border-white/30 text-white font-medium px-6 py-2.5 rounded-lg text-sm hover:bg-white/10 transition-colors" data-testid="cta-map-btn">
              Browse the map
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
